import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '@/services/queryClient'
import { createRole, deleteRole, fetchPermissions, fetchRoles, updateRole } from './api'

/**
 * The permission catalogue.
 *
 * Seeded reference data that changes only with a deploy, so it is held for the
 * session rather than re-read. Every role form reads the same copy.
 */
export function usePermissionCatalogue() {
  return useQuery({
    queryKey: queryKeys.permissions,
    queryFn: ({ signal }) => fetchPermissions(signal),
    staleTime: Infinity,
  })
}

export function useRoles() {
  return useQuery({
    queryKey: queryKeys.roles,
    queryFn: ({ signal }) => fetchRoles(signal),
    staleTime: 30_000,
  })
}

/**
 * Create one.
 *
 * Not retried: a duplicate name or an unknown permission id is an answer, and
 * sending it again gets the same one. Worse, a create that actually succeeded
 * and merely failed to report would be made twice.
 */
export function useCreateRole() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: createRole,
    retry: false,
    onSuccess: () => client.invalidateQueries({ queryKey: queryKeys.roles }),
  })
}

/**
 * Update and delete.
 *
 * Neither is retried, for the same reason create is not: every way these fail is
 * an answer — a taken name, a system role, a role somebody holds — and asking
 * again gets the same one.
 */
export function useUpdateRole() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: updateRole,
    retry: false,
    onSuccess: () => client.invalidateQueries({ queryKey: queryKeys.roles }),
  })
}

export function useDeleteRole() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: deleteRole,
    retry: false,
    onSuccess: () => client.invalidateQueries({ queryKey: queryKeys.roles }),
  })
}
